/**
 * AG-UI Event Handler
 *
 * Processes AG-UI protocol events (RUN_STARTED, TEXT_MESSAGE_*, TOOL_CALL_*,
 * CUSTOM thinking/UI actions, RUN_FINISHED, RUN_ERROR).
 * Returns a handler function and reset function for use in the streaming loop.
 */

import { useCallback, useMemo, useRef } from 'react';

import { generateMessageId, parseSSELineAsAGUI } from './sseHelpers';
import type { SolomonAgentMessage, StreamState, UIActionEvent } from './useSolomonAgent';
import { toolRegistry } from '../../lib/solomon/toolResultRegistry';
import { AGUIEventType, SolomonCustomEventName } from '../../types/agui';
import type {
  AGUIEvent,
  TextMessageContentEvent,
  ToolCallStartEvent,
  ToolCallResultEvent,
  RunFinishedEvent,
  RunErrorEvent,
  CustomEvent,
  UIActionPlanPayload,
  UIActionStartPayload,
  UIActionCompletePayload,
  UIActionErrorPayload,
} from '../../types/agui';

export type AGUIHandlerCallbacks = {
  onStreamStateUpdate: (update: Partial<StreamState>) => void;
  onMessageComplete: (message: SolomonAgentMessage) => void;
  onStoreMessage: (content: string, thinking: string | undefined, toolUses: SolomonAgentMessage['toolUses']) => void;
  onAGUIEvent?: (event: AGUIEvent) => void;
  onUIActionEvent?: (event: UIActionEvent) => void;
  onError?: (error: Error) => void;
};

export type AGUIHandlerReturn = {
  handleLine: (line: string) => void;
  handleStreamDone: () => void;
  reset: () => void;
  getAssistantContent: () => string;
  wasMessageCreated: () => boolean;
};

export function useAGUIEventHandler(callbacks: AGUIHandlerCallbacks): AGUIHandlerReturn {
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const assistantContentRef = useRef('');
  const thinkingContentRef = useRef('');
  const toolUsesRef = useRef<NonNullable<SolomonAgentMessage['toolUses']>>([]);
  const messageCreatedRef = useRef(false);

  const reset = useCallback(() => {
    assistantContentRef.current = '';
    thinkingContentRef.current = '';
    toolUsesRef.current = [];
    messageCreatedRef.current = false;
  }, []);

  const createAndEmitMessage = useCallback((sessionId?: string) => {
    const cb = cbRef.current;
    const message: SolomonAgentMessage = {
      id: generateMessageId(),
      role: 'assistant',
      content: assistantContentRef.current,
      thinking: thinkingContentRef.current || undefined,
      toolUses: toolUsesRef.current.length > 0 ? [...toolUsesRef.current] : undefined,
      timestamp: new Date(),
    };

    cb.onStoreMessage(
      assistantContentRef.current,
      thinkingContentRef.current || undefined,
      toolUsesRef.current.length > 0 ? [...toolUsesRef.current] : undefined
    );

    cb.onStreamStateUpdate({
      isStreaming: false,
      isThinking: false,
      currentResponse: '',
      currentThinking: '',
      activeToolUse: null,
      ...(sessionId ? { sessionId } : {}),
    });

    cb.onMessageComplete(message);
    messageCreatedRef.current = true;
    toolUsesRef.current = [];
    thinkingContentRef.current = '';
  }, []);

  const handleLine = useCallback(
    (line: string) => {
      const cb = cbRef.current;
      const aguiEvent = parseSSELineAsAGUI(line);
      if (!aguiEvent) return;

      cb.onAGUIEvent?.(aguiEvent);

      switch (aguiEvent.type) {
        case AGUIEventType.RUN_STARTED:
          break;

        case AGUIEventType.TEXT_MESSAGE_START:
          cb.onStreamStateUpdate({ isThinking: false });
          break;

        case AGUIEventType.TEXT_MESSAGE_CONTENT: {
          const contentEvent = aguiEvent as TextMessageContentEvent;
          if (contentEvent.delta) {
            assistantContentRef.current += contentEvent.delta;
            cb.onStreamStateUpdate({
              isThinking: false,
              currentResponse: assistantContentRef.current,
            });
          }
          break;
        }

        case AGUIEventType.TEXT_MESSAGE_END:
          break;

        case AGUIEventType.TOOL_CALL_START: {
          const toolEvent = aguiEvent as ToolCallStartEvent;
          toolUsesRef.current.push({
            tool: toolEvent.toolCallName,
            toolCallId: toolEvent.toolCallId,
            input: undefined,
          });
          cb.onStreamStateUpdate({
            isThinking: true,
            activeToolUse: {
              tool: toolEvent.toolCallName,
              toolCallId: toolEvent.toolCallId,
              input: undefined,
            },
          });
          break;
        }

        case AGUIEventType.TOOL_CALL_RESULT: {
          const resultEvent = aguiEvent as ToolCallResultEvent;
          const idx = toolUsesRef.current.findIndex((tu) => tu.toolCallId === resultEvent.toolCallId);
          if (idx >= 0) {
            let parsedResult: unknown = resultEvent.content;
            try {
              parsedResult = JSON.parse(resultEvent.content);
            } catch {
              // keep raw string
            }
            toolUsesRef.current[idx].result = parsedResult;
            toolRegistry.handle(toolUsesRef.current[idx].tool, parsedResult, {
              toolCallId: resultEvent.toolCallId,
            });
          }
          break;
        }

        case AGUIEventType.TOOL_CALL_END:
          cb.onStreamStateUpdate({ activeToolUse: null });
          break;

        case AGUIEventType.CUSTOM: {
          const customEvent = aguiEvent as CustomEvent;

          if (customEvent.name === SolomonCustomEventName.THINKING_CONTENT) {
            const payload = customEvent.value as { thinkingId?: string; delta?: string };
            if (payload?.delta) {
              thinkingContentRef.current += payload.delta;
              cb.onStreamStateUpdate({ currentThinking: thinkingContentRef.current });
            }
          }

          if (customEvent.name === SolomonCustomEventName.UI_ACTION_PLAN) {
            cb.onUIActionEvent?.({ type: 'UI_ACTION_PLAN', payload: customEvent.value as UIActionPlanPayload });
          } else if (customEvent.name === SolomonCustomEventName.UI_ACTION_START) {
            cb.onUIActionEvent?.({ type: 'UI_ACTION_START', payload: customEvent.value as UIActionStartPayload });
          } else if (customEvent.name === SolomonCustomEventName.UI_ACTION_COMPLETE) {
            cb.onUIActionEvent?.({ type: 'UI_ACTION_COMPLETE', payload: customEvent.value as UIActionCompletePayload });
          } else if (customEvent.name === SolomonCustomEventName.UI_ACTION_ERROR) {
            cb.onUIActionEvent?.({ type: 'UI_ACTION_ERROR', payload: customEvent.value as UIActionErrorPayload });
          }
          break;
        }

        case AGUIEventType.RUN_FINISHED: {
          const finishedEvent = aguiEvent as RunFinishedEvent;
          const result = finishedEvent.result as { sessionId?: string } | undefined;
          createAndEmitMessage(result?.sessionId);
          break;
        }

        case AGUIEventType.RUN_ERROR: {
          const errorEvent = aguiEvent as RunErrorEvent;
          const error = new Error(errorEvent.message || 'Unknown error');
          cb.onStreamStateUpdate({ isStreaming: false, isThinking: false, error });
          cb.onError?.(error);
          break;
        }

        default:
          break;
      }
    },
    [createAndEmitMessage]
  );

  const handleStreamDone = useCallback(() => {
    const cb = cbRef.current;
    if (assistantContentRef.current && !messageCreatedRef.current) {
      createAndEmitMessage();
    }
    cb.onStreamStateUpdate({
      isStreaming: false,
      isThinking: false,
      currentResponse: '',
      currentThinking: '',
    });
  }, [createAndEmitMessage]);

  return useMemo(
    () => ({
      handleLine,
      handleStreamDone,
      reset,
      getAssistantContent: () => assistantContentRef.current,
      wasMessageCreated: () => messageCreatedRef.current,
    }),
    [handleLine, handleStreamDone, reset]
  );
}
